import { AuthService } from './../data/services/AuthService';
import { useSnackbar } from '~~/stores/snackbar';
import { useUser } from '~~/stores/user';
import { Role } from '@/data/enums/Role';
import { RoutePermissions } from '@/data/RoutePermissions';
import { RouteLocationNormalized, RouteLocationRaw, NavigationFailure } from 'vue-router';
import { useLogs } from '~~/composables/useLogs';

export default defineNuxtRouteMiddleware(async (to, from) => {
  useLogs().trackPageView(to);

  // Set redirect head content
  setHeadContent(to);

  // If path is not protected, then navigate to location.
  const unprotectedPaths = RoutePermissions.values.filter((path) => path.permissions === null);

  if (unprotectedPaths.some((path) => path.path === to.path)) {
    return true;
  }

  // If protected path, validate user logged in.
  await handleLogin();

  // handle root path redirect based on role.
  if (to.path === '/') {
    return handleRootPathRedirect();
  }

  // get normalised route that's being accessed.
  const routeBeingAccessed = to.path.endsWith('/') ? to.path.slice(0, -1) : to.path;

  // Handle /admin path redirect based on role.
  if (routeBeingAccessed === '/admin') {
    return handleAdminPathRedirect();
  }

  // Service alerts are behind a feature flag; treat its routes as non-existent when disabled.
  if (routeBeingAccessed.startsWith('/admin/service-alerts') && !useConfiguration().FEATURE_SERVICE_ALERTS) {
    return handleIncorrectRoute();
  }

  // Get permissions for the route being accessed.
  let routePermissions = RoutePermissions.values.find(
    (route) => route.path === routeBeingAccessed
  )?.permissions;

  // Check if a route exists, else we throw errors
  let routeExists = RoutePermissions.values.some((e) => e.path === routeBeingAccessed);

  // Validate routes against naviagte ID value
  if (to.params.id) {
    const tempRoutes = RoutePermissions.values.map(function (route) {
      return {
        path: route.path.replace(':id', to.params.id.toString()),
        permissions: route.permissions,
      };
    });

    // Check if a route exists, else we throw errors
    if (tempRoutes.some((route) => route.path === routeBeingAccessed)) {
      routeExists = true;
    }

    routePermissions = tempRoutes.find((route) => route.path === routeBeingAccessed)?.permissions;
  }

  if (to.params.profileId) {
    const tempRoutes = RoutePermissions.values.map(function (route) {
      return {
        path: route.path
          .replace(':profileId', to.params.profileId.toString())
          .replace(':themeId', to.params.themeId.toString()),
        permissions: route.permissions,
      };
    });

    // Check if a route exists, else we throw errors
    if (tempRoutes.some((route) => route.path === routeBeingAccessed)) {
      routeExists = true;
    }

    routePermissions = tempRoutes.find((route) => route.path === routeBeingAccessed)?.permissions;
  }

  // If route doesn't exist show notification, and redirect to root path.
  if (!routeExists) {
    return handleIncorrectRoute();
  }

  // If normalized route has no permissions - navigate to route
  if (!routePermissions || !routePermissions.length) {
    return true;
  }

  // If route protected and user has permission - navigate to route.
  if (RoutePermissions.hasPermissions(routePermissions)) {
    return true;
  }

  return handleInvalidPermissions();
});

async function handleLogin(): Promise<void> {
  if (!useUser().isSet || !useIsAuthenticated().value) {
    await AuthService.instance.loginByRedirect();
  }
}

function handleRootPathRedirect(): RouteLocationRaw | Promise<void | NavigationFailure> {
  switch (useUser().role) {
    case Role.TripleAdmin:
      return navigateTo('/admin/employees');
    case Role.EmployerAdmin:
    case Role.Viewer:
    case Role.TripleaReadonlyAdmin:
      return navigateTo('/admin/my-employer');
    case Role.Author:
      return navigateTo('/admin/articles');
    default:
      return navigateTo('/dashboard');
  }
}

function handleAdminPathRedirect(): RouteLocationRaw | Promise<void | NavigationFailure> {
  switch (useUser().role) {
    case Role.EmployerAdmin:
    case Role.TripleAdmin:
    case Role.TripleaReadonlyAdmin:
      return navigateTo('/admin/employees');
    case Role.Viewer:
      return navigateTo('/admin/teams');
    case Role.Author:
      return navigateTo('/admin/articles');
    default:
      return handleInvalidPermissions();
  }
}

function handleIncorrectRoute(): RouteLocationRaw | Promise<void | NavigationFailure> {
  return navigateTo('/404');
}

function handleInvalidPermissions(): RouteLocationRaw | Promise<void | NavigationFailure> {
  return navigateTo('/403');
}

function setHeadContent(to: RouteLocationNormalized): void {
  useHead({
    title: `Triple A - ${to.meta.title}`,
    meta: [{ name: 'og:title', content: `Triple A - ${to.meta.title}` }],
  });
}
